/**
 * ClassRecordings — admin-first list of every ingested Jitsi/JaaS recording.
 *
 * Renders inside the Teacher Dashboard (as a modal launched from a quick-link)
 * and can also be embedded read-only in the student SchoolDashboard.
 *
 * Playback authentication: <video src> can't send Authorization headers, so
 * we pass `?auth=<token>` on the stream URL — the backend accepts either.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Video, Loader2, X, Play, Upload, Edit3, Trash2, Check } from 'lucide-react';
import api from '../services/api';
import { toast } from 'sonner';

const token = () => localStorage.getItem('academy_token') || '';
const authHeaders = () => ({ Authorization: `Bearer ${token()}` });

const backendUrl = process.env.REACT_APP_BACKEND_URL;
const streamUrl = (id) => `${backendUrl}/api/live/recording/${id}/stream?auth=${encodeURIComponent(token())}`;

const fmtDate = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return iso || '—'; } };
const fmtBytes = (n) => {
  if (!n) return '—';
  const kb = n / 1024, mb = kb / 1024;
  return mb >= 1 ? `${mb.toFixed(1)} MB` : `${kb.toFixed(0)} KB`;
};
const fmtGrades = (gs) => (gs && gs.length ? gs.map((g) => (g === 0 ? 'K' : `G${g}`)).join(', ') : 'All grades');

const ClassRecordings = () => {
  const [open, setOpen] = useState(false);
  const [recs, setRecs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [playing, setPlaying] = useState(null);   // full recording object
  const [ingestOpen, setIngestOpen] = useState(false);
  const [ingestForm, setIngestForm] = useState({ source_url: '', title: '', room: '', grade_group: '', duration_seconds: '' });
  const [ingesting, setIngesting] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/live/recordings?limit=50', { headers: authHeaders() });
      setRecs(r.data.recordings || []);
      setIsAdmin(!!r.data.is_admin);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load recordings');
    } finally { setLoading(false); }
  };

  useEffect(() => { if (open) load(); }, [open]);

  const ingest = async () => {
    if (!ingestForm.source_url) return toast.error('Paste the recording URL first.');
    setIngesting(true);
    try {
      const grades = ingestForm.grade_group
        ? ingestForm.grade_group.split(',').map((g) => parseInt(g.trim(), 10)).filter(Number.isFinite)
        : null;
      const dur = parseInt(ingestForm.duration_seconds, 10);
      await api.post('/live/admin/recording/ingest', {
        source_url: ingestForm.source_url.trim(),
        title: ingestForm.title || undefined,
        room: ingestForm.room || undefined,
        grade_group: grades && grades.length ? grades : undefined,
        duration_seconds: Number.isFinite(dur) ? dur : undefined,
      }, { headers: authHeaders() });
      toast.success('Recording ingested to library.');
      setIngestForm({ source_url: '', title: '', room: '', grade_group: '', duration_seconds: '' });
      setIngestOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Ingest failed. Verify the URL is downloadable.');
    } finally { setIngesting(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this recording from the library?')) return;
    try {
      await api.patch(`/live/admin/recording/${id}`, { is_deleted: true }, { headers: authHeaders() });
      toast.success('Recording removed.');
      setRecs((prev) => prev.filter((r) => r.id !== id));
      if (playing?.id === id) setPlaying(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed.');
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 border-2 border-purple-500 bg-purple-500 hover:bg-purple-400 text-white px-4 py-2 text-[10px] font-black uppercase tracking-widest"
        data-testid="link-class-recordings"
      >
        <Video className="w-3.5 h-3.5" /> Class Recordings
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/85"
          onClick={() => { setOpen(false); setPlaying(null); }}
          data-testid="class-recordings-modal"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-5xl max-h-[92vh] overflow-hidden flex flex-col bg-zinc-950 border-2 border-purple-500/60"
          >
            <div className="border-b-2 border-purple-500/40 px-5 py-3 flex items-center justify-between">
              <div className="flex items-center gap-2 text-purple-400">
                <Video className="w-4 h-4" />
                <span className="text-[10px] font-black uppercase tracking-[0.28em]">Class Recordings</span>
              </div>
              <div className="flex items-center gap-2">
                {isAdmin && (
                  <button
                    onClick={() => setIngestOpen(!ingestOpen)}
                    className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest border border-purple-500/50 hover:border-purple-400 text-purple-300 px-2 py-1"
                    data-testid="class-rec-ingest-toggle"
                  >
                    <Upload className="w-3 h-3" /> Manual Ingest
                  </button>
                )}
                <button onClick={() => { setOpen(false); setPlaying(null); }} className="text-zinc-400 hover:text-white p-1" data-testid="class-rec-close">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-5 space-y-4">
              {/* Ingest form (admin) */}
              {isAdmin && ingestOpen && (
                <div className="border-2 border-purple-500/40 bg-purple-500/5 p-4 space-y-3" data-testid="class-rec-ingest-form">
                  <div className="text-[10px] font-black uppercase tracking-[0.24em] text-purple-300">Paste a recording URL — we'll copy it into the library.</div>
                  <input
                    type="url"
                    value={ingestForm.source_url}
                    onChange={(e) => setIngestForm({ ...ingestForm, source_url: e.target.value })}
                    placeholder="https://jaas.8x8.com/... (signed MP4 URL)"
                    className="w-full bg-black border-2 border-zinc-800 focus:border-purple-500 outline-none px-3 py-2 text-sm text-white"
                    data-testid="class-rec-ingest-url"
                  />
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    <input placeholder="Title" value={ingestForm.title} onChange={(e) => setIngestForm({ ...ingestForm, title: e.target.value })} className="bg-black border-2 border-zinc-800 focus:border-purple-500 outline-none px-3 py-2 text-sm text-white" data-testid="class-rec-ingest-title" />
                    <input placeholder="Room" value={ingestForm.room} onChange={(e) => setIngestForm({ ...ingestForm, room: e.target.value })} className="bg-black border-2 border-zinc-800 focus:border-purple-500 outline-none px-3 py-2 text-sm text-white" data-testid="class-rec-ingest-room" />
                    <input placeholder="Grades (e.g. 5,6,7,8)" value={ingestForm.grade_group} onChange={(e) => setIngestForm({ ...ingestForm, grade_group: e.target.value })} className="bg-black border-2 border-zinc-800 focus:border-purple-500 outline-none px-3 py-2 text-sm text-white" data-testid="class-rec-ingest-grades" />
                    <input placeholder="Duration (sec)" value={ingestForm.duration_seconds} onChange={(e) => setIngestForm({ ...ingestForm, duration_seconds: e.target.value })} className="bg-black border-2 border-zinc-800 focus:border-purple-500 outline-none px-3 py-2 text-sm text-white" data-testid="class-rec-ingest-duration" />
                  </div>
                  <button
                    onClick={ingest}
                    disabled={ingesting}
                    className="inline-flex items-center gap-2 bg-purple-500 hover:bg-purple-400 text-white px-4 py-2 text-[10px] font-black tracking-widest uppercase disabled:opacity-60"
                    data-testid="class-rec-ingest-submit"
                  >
                    {ingesting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                    {ingesting ? 'Ingesting…' : 'Ingest'}
                  </button>
                </div>
              )}

              {/* Player */}
              {playing && (
                <div className="border-2 border-purple-500/40 p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <div className="text-[10px] font-black uppercase tracking-widest text-purple-400">Now Playing</div>
                      <div className="text-lg font-black text-white">{playing.title}</div>
                      <div className="text-[10px] text-zinc-500">
                        {fmtDate(playing.recorded_at)} · {fmtGrades(playing.grade_group)} · {fmtBytes(playing.size)}
                      </div>
                    </div>
                    <button onClick={() => setPlaying(null)} data-testid="class-rec-close-player" className="text-zinc-400 hover:text-white p-1"><X className="w-4 h-4" /></button>
                  </div>
                  <video
                    src={streamUrl(playing.id)}
                    controls
                    autoPlay
                    className="w-full max-h-[55vh] bg-black"
                    data-testid="class-rec-video"
                  />
                </div>
              )}

              {/* Loading */}
              {loading && (
                <div className="flex items-center justify-center py-12 text-zinc-500 text-sm">
                  <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading recordings…
                </div>
              )}

              {/* List */}
              {!loading && (
                <div>
                  <div className="text-[10px] font-black uppercase tracking-widest text-zinc-500 mb-2">
                    Recent recordings ({recs.length})
                  </div>
                  {recs.length === 0 ? (
                    <div className="p-8 text-center border-2 border-dashed border-zinc-800">
                      <Video className="w-8 h-8 mx-auto mb-3 text-zinc-600" />
                      <div className="text-white font-black text-lg">No recordings yet.</div>
                      <div className="text-sm text-zinc-500 mt-1">
                        Once Jitsi finishes uploading a class recording it'll appear here automatically.
                      </div>
                    </div>
                  ) : (
                    <div className="border-2 border-zinc-800 divide-y divide-zinc-800">
                      {recs.map((r) => (
                        <div key={r.id} className="px-3 py-2 flex items-center justify-between gap-3">
                          <div className="min-w-0 flex-1">
                            <div className="font-bold text-white truncate">{r.title}</div>
                            <div className="text-[10px] text-zinc-500 truncate">
                              {fmtDate(r.recorded_at)} · {fmtGrades(r.grade_group)}
                              {r.room ? ` · ${r.room}` : ''} · {fmtBytes(r.size)}
                            </div>
                          </div>
                          <div className="flex items-center gap-1 flex-shrink-0">
                            <button
                              onClick={() => setPlaying(r)}
                              className="inline-flex items-center gap-1 border border-purple-500/50 text-purple-300 hover:bg-purple-500/10 px-2 py-1 text-[10px] font-black tracking-widest uppercase"
                              data-testid={`class-rec-play-${r.id}`}
                            >
                              <Play className="w-3 h-3" /> Play
                            </button>
                            {isAdmin && (
                              <button
                                onClick={() => remove(r.id)}
                                className="inline-flex items-center gap-1 border border-red-500/40 text-red-400 hover:bg-red-500/10 px-2 py-1 text-[10px] font-black tracking-widest uppercase"
                                data-testid={`class-rec-delete-${r.id}`}
                              >
                                <Trash2 className="w-3 h-3" />
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ClassRecordings;
